
import { useEffect } from 'react';
import { GalleryItem } from '../../../lib/supabase';

interface GalleryLightboxProps {
  item: GalleryItem;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}

export default function GalleryLightbox({ item, onClose, onPrev, onNext, hasPrev, hasNext }: GalleryLightboxProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  const isYouTube = item.url.includes('youtube.com/embed') || item.url.includes('youtu.be');
  const isVimeo = item.url.includes('vimeo.com');

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-all cursor-pointer z-10"
      >
        <i className="ri-close-line text-xl"></i>
      </button>

      {/* Title */}
      <div className="absolute top-5 left-1/2 -translate-x-1/2 z-10">
        <span className="text-white text-sm font-medium bg-black/50 px-4 py-2 rounded-full">
          {item.title}
        </span>
      </div>

      {/* Prev */}
      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-cyan-400/30 border border-white/10 hover:border-cyan-400/50 rounded-full text-white transition-all cursor-pointer z-10"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
      )}

      {/* Next */}
      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-cyan-400/30 border border-white/10 hover:border-cyan-400/50 rounded-full text-white transition-all cursor-pointer z-10"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      )}

      {/* Content */}
      <div
        className="relative max-w-5xl w-full mx-16 max-h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {item.type === 'photo' ? (
          <img
            src={item.url}
            alt={item.title}
            className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
          />
        ) : (isYouTube || isVimeo) ? (
          <div className="w-full aspect-video rounded-xl overflow-hidden shadow-2xl">
            <iframe
              src={item.url}
              title={item.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        ) : (
          <video
            src={item.url}
            controls
            autoPlay
            className="max-w-full max-h-[85vh] rounded-xl shadow-2xl"
          />
        )}
      </div>
    </div>
  );
}
